/**
 * proof. SPA entry point.
 *
 * Architecture.md §4.1: Vite + React 18 + React Router v7 (SPA mode).
 * Architecture.md §4.6: Workbox SW registered here.
 *
 * Design.md §5.3: Themes initialised from localStorage before first paint.
 */

import { StrictMode, lazy, Suspense } from "react";
import { createRoot } from "react-dom/client";
import { createBrowserRouter, RouterProvider, Navigate } from "react-router";

import { initTheme } from "~/hooks/useTheme";
import { startVersionProbe } from "~/features/pwa/version-probe";
import { startUpdateChecks } from "~/features/pwa/update-controller";
import { registerStaleChunkRescue } from "~/features/pwa/stale-chunk-rescue";
import "@proof/ui/tokens";
// Glass surface utilities (.glass / .glass-strong / .glass-deep) — required by
// the desktop right-rail (RecipeRail) and any other glass-treated chrome.
// See packages/ui/src/glass.css; the prefers-reduced-transparency fallback
// in that file maintains readability without backdrop-filter.
import "@proof/ui/glass";
// Disable iOS Safari pinch-zoom — runs as a side-effect before first render.
// iOS Safari ignores user-scalable=no in the viewport meta (since iOS 10).
// JS gesture event handlers are the only reliable prevention. See the module
// comment for the operator-authorised accessibility trade-off.
import "~/lib/disable-pinch-zoom";
// Track 4.R — crash capture: registers window.onerror + onunhandledrejection
// as a module side-effect, and exports the ErrorBoundary component.
import { ErrorBoundary } from "~/crashes/client";
// PWA auto-update — headless component that registers the Workbox SW via
// useRegisterSW() (vite-plugin-pwa) and silently applies new bundles at a
// safe moment. No prompt, no UI. See the mount comment below.
import { AutoUpdater } from "~/features/pwa/AutoUpdater";
// Wave PERF-1 — recipe-open instrumentation. installWindowSeam exposes
// window.__perf for headless capture. PerfOverlay renders only when
// `?perf=1` is in the URL AND the build is DEV / opt-in profile.
import { installWindowSeam } from "~/lib/perf";
import { PerfOverlay } from "~/lib/PerfOverlay";

// Install the window.__perf seam early so any mark() call below has
// somewhere to land. No-op in production builds.
installWindowSeam();

// Initialise theme before React mounts — prevents FOUC on styled content.
// Reads proof-theme + proof-appearance from localStorage; applies classes to
// document.documentElement. Must run before first render.
initTheme();

// ── Activate the brand font stylesheet (A9 fix, QA 2026-06-11) ───────────────
// index.html preloads the Google Fonts CSS with rel="preload" (non-blocking,
// the F2 perf fix). The rel="stylesheet" swap used to be an inline `onload=`
// attribute, which the hash-pinned CSP blocks (inline event handlers need
// 'unsafe-hashes') — so the brand typefaces silently never applied and every
// load logged a CSP violation. The swap now runs here, from the bundled
// module ('self' is always script-src-allowed). Flipping rel before the
// preload finishes is safe: the browser applies the stylesheet when the
// fetch completes; until then the system font stack covers rendering.
function activateFontStylesheet(): void {
  const link = document.getElementById("proof-fonts-css");
  if (link instanceof HTMLLinkElement && link.rel !== "stylesheet") {
    link.rel = "stylesheet";
  }
}
activateFontStylesheet();

// Start the version probe (Layer 4 — PWA absolute version update).
// Polls /__app-version.json on visibility change and every 15 min while
// visible. Sets an external flag that AutoUpdater ORs into its apply
// condition alongside the SW-based needRefresh flag.
// No-ops if navigator is unavailable (SSR-safe, defensive).
startVersionProbe();

// Start explicit, throttled service-worker update checks (fix-pwa-update-
// delivery). Forces registration.update() on launch (after serviceWorker.ready)
// and on every visibilitychange→visible + window focus, throttled to ≥ 60 s
// apart. This is the iOS-standalone-critical piece: a relaunched standalone
// PWA is a fresh webview but not a fresh navigation, so the browser does not
// re-fetch sw.js on its own — without this nudge a new deployment is never
// detected across close/reopen cycles. No-ops when navigator is unavailable.
startUpdateChecks();

// Register the stale-chunk rescue listener. On vite:preloadError (dynamic chunk
// fails to fetch — classic stale-shell symptom), automatically runs the
// purgeCachesAndReload() sequence on the first failure within a 5-minute guard
// window. On a second failure within the window, flags the external needRefresh
// store instead (loop protection) so AutoUpdater schedules a clean apply.
// See features/pwa/stale-chunk-rescue.ts.
registerStaleChunkRescue();

// ─── Route imports (lazy-load in Phase 3; eager here for smoke tests) ─────────

// Phase 2b capsule prototype — dev-only, never bundled in production.
// Lazy-loaded behind import.meta.env.DEV so Vite tree-shakes it in prod.
const ShellPrototypePage = import.meta.env.DEV
  ? lazy(() => import("~/features/shell-prototype/index").then((m) => ({ default: m.default })))
  : null;

// /pip-demo — Pip mascot QA surface (dev only, plan Wave 1).
// Lazy-loaded behind import.meta.env.DEV so Vite tree-shakes it in prod.
// DEV-guard pattern mirrors ShellPrototypePage above.
const PipDemoRoute = import.meta.env.DEV
  ? lazy(() => import("~/routes/pip-demo").then((m) => ({ default: m.default })))
  : null;

import RootIndex from "~/routes/index";
import SignInRoute from "~/routes/sign-in";
import SignUpRoute from "~/routes/sign-up";
import NotFound from "~/routes/not-found";
import AppRoute from "~/routes/app";
// The child-route table for the single `/app` route object (route unification,
// Phase C). Shared with the unit tests so they exercise the shipped shape.
import { APP_ROUTE_CHILDREN } from "~/routes/app-routes";

// /reset-password — destination of the password-reset email link.
// Lazy-loaded: only reached when a user clicks through the email so it
// should never appear on the /app first-paint critical path. Pattern matches
// the Share-view lazy imports above.
const ResetPasswordRoute = lazy(() => import("~/routes/reset-password"));

// Wave PERF-2.A — lazy-load the Share-view + public profile routes so the
// `/app` first-paint critical path stops shipping their bundles. The two live
// public routes (`/r/:publicUuid` recipe Share view, `/u/:handle` profile) are
// only reached by unauthenticated readers; holding them in the main chunk costs
// gz weight (perf-baseline §1 win 4) on every authenticated `/app` navigation.
//
// R1 dead-UI audit (roadmap 2026-07-03 §R1): the placeholder ShareLayout stub
// and its `/c/:uuid`, `/batch/:uuid`, `/a/:uuid`, `/u/:handle/:slug` (+ bare
// `/r`, `/u`) routes were deleted — they rendered the raw pathname, not a real
// Share view, so unknown paths now fall through to the NotFound catch-all.
// R4 WP-1 landed `/u/:handle/:slug` for real, on the SAME RecipeShareView
// component as `/r/:publicUuid` (not a stub). `/c/`, `/batch/` and `/a/` remain
// deleted and still fall through to NotFound.
//
// Pattern matches the existing admin lazy-load block below. Each lazy import
// emits its own chunk; they are statically `import.meta.env`-independent so
// Rollup tree-shakes them out of the main bundle in production.
const RecipeShareView = lazy(() => import("~/routes/share/recipe"));
const UserProfileView = lazy(() => import("~/routes/user-profile"));
// B-WP7 — Discover-lite: public /discover boards. Lazy-loaded per PERF-2.A; a
// public acquisition surface reached from the Share-page footer (deliberately NO
// primary-nav entry this WP — see share/discover.tsx / feature plan B-WP7 §d).
const DiscoverView = lazy(() => import("~/routes/share/discover"));
// B-WP12 — public groups directory (/groups) + public group page (/g/:uuid).
// Public surfaces reachable signed-out (like /r/ and /u/); lazy-loaded off the
// authed /app first-paint path per the PERF-2.A pattern above.
const GroupsDirectoryView = lazy(() => import("~/routes/groups-directory"));
const PublicGroupView = lazy(() => import("~/routes/public-group"));
// R4 WP-2 — public grammar reference (/docs/syntax). Unauthenticated: renders
// the same CI-verified SyntaxHelp component as the in-app /app/help page, so a
// signed-out reader arriving from the marketing footer resolves no session and
// can hit no sign-in redirect. Lazy per PERF-2.A — never on the authed /app
// first-paint path. `/docs/*` is claimed for the "web" target in
// @proof/domain/urls/route-targets, ahead of the marketing rule.
const DocsSyntaxView = lazy(() => import("~/routes/docs-syntax"));
// Wave 2 (onboarding-empty-r1): OnboardingDemo route removed — the guided
// wizard is wishlisted per `.plan/wishlist/onboarding-wizard.md`.
// Wave 7 (2026-05-12): all *-demo routes removed (recipe-page-demo,
// library-r1-demo, editor-demo, workspace-demo, explorer-demo,
// rating-schema-demo). Workspace is the production recipe surface; the demos
// are wishlisted at .plan/wishlist/2026-05-12-dev-demo-routes.md for revival
// under /_dev/*.
// Track 4.P — admin panel shell + pages
// §4.9.2: Lazy-loaded so Vite emits separate admin-* chunks that are
// excluded from the Workbox precache manifest. The admin surface is
// invite-only; a null fallback during the async load is acceptable.
const AdminGuard = lazy(() =>
  import("~/features/admin/AdminGuard").then((m) => ({ default: m.AdminGuard })),
);
// Admin console (Direction B). The five-section IA — Overview · Analytics ·
// People · Queue · System — is a re-composition of the existing admin RPCs
// behind a cleaner shell. Lazy like every admin surface so its chunks stay off
// the `/app` first-paint bundle and out of the Workbox precache manifest.
const AdminShell = lazy(() =>
  import("~/features/admin/console/ConsoleShell").then((m) => ({ default: m.ConsoleShell })),
);
const AdminOverview = lazy(() =>
  import("~/features/admin/console/Overview").then((m) => ({ default: m.Overview })),
);
const AdminPeople = lazy(() =>
  import("~/features/admin/console/People").then((m) => ({ default: m.People })),
);
const AdminQueue = lazy(() =>
  import("~/features/admin/console/Queue").then((m) => ({ default: m.Queue })),
);
const AdminSystem = lazy(() =>
  import("~/features/admin/console/System").then((m) => ({ default: m.System })),
);
const AdminAnalytics = lazy(() =>
  import("~/features/admin/console/Analytics").then((m) => ({ default: m.Analytics })),
);
// Custom logic (`/app/settings/custom-logic`), Syntax help (`/app/help`),
// Cooking ledger (`/app/ledger`), and Family groups (`/app/g*`) are rendered
// IN-SHELL by WorkspaceContainer (routes/app.tsx) — their lazy imports live
// there now (shell-persistence fix, 2026-07-16), off the `/app` first-paint
// critical path, so the app shell (wordmark top bar + NavTray) stays mounted
// across every tray destination.
const JoinRoute = lazy(() => import("~/routes/join"));
// Friend-invite landing (B-WP11c) — personal invite link → active friendship.
// A pre-auth email landing, kept standalone (not a tray destination).
const FriendInviteRoute = lazy(() => import("~/routes/friend-invite"));

// ─── Router (SPA mode — no SSR loaders) ──────────────────────────────────────
// Architecture.md §3.1: URL scheme
//   /app/*           → authenticated Workspace (placeholder; full chrome Phase 3)
//   /r/:publicUuid   → share view: recipe by permanent uuid link (live)
//   /u/:handle       → public user profile (live)
//   /u/:handle/:slug → share view: recipe by canonical slug (live, R4 WP-1)
//   (collection / batch / artefact Share URLs remain deleted)
//
// Navigation requests inside the SPA are handled by the router. The Service
// Worker's NavigationRoute falls through to the precached /index.html for
// direct URL loads, satisfying the offline-shell requirement.

/**
 * Legacy /admin/users/:userUuid → People search redirect. The person detail is
 * now a drawer inside People; an old bookmarked user link resolves into the
 * People search that surfaces that account.
 */
function LegacyUserRedirect() {
  const uuid = window.location.pathname.split("/").filter(Boolean).pop() ?? "";
  return <Navigate to={`/admin/people?q=${encodeURIComponent(uuid)}`} replace />;
}

const router = createBrowserRouter([
  {
    path: "/",
    element: <RootIndex />,
    errorElement: <NotFound />,
  },
  // /sign-in — dedicated auth route. Navigates to /app on session established.
  // sign-out redirects here so the URL observably changes (spec step 8).
  {
    path: "/sign-in",
    element: <SignInRoute />,
  },
  // /sign-up — dedicated account-creation route. Renders SignInGate in signup
  // mode so the invite-token + registration form is the first thing the user sees.
  // Navigates to /app on session established (REQUIRE_EMAIL_VERIFICATION=false on dev;
  // on prod the server returns a verify-your-email state before the session is issued).
  {
    path: "/sign-up",
    element: <SignUpRoute />,
  },
  // /reset-password — destination of the password-reset email link.
  // The reset email links here with `?token=<jwt>`. The route reads the token
  // from useSearchParams, POSTs `{ token, newPassword }` to the api wrapper at
  // /auth/password-reset/confirm, and redirects to /sign-in?reset=ok on success.
  // Plan ref: .plan/features/2026-05-23-wire-password-reset-frontend.md §4.2.
  {
    path: "/reset-password",
    element: (
      <Suspense fallback={null}>
        <ResetPasswordRoute />
      </Suspense>
    ),
  },
  // /app — the ONE authenticated route object (route unification, 2026-07-24;
  // plan `.plan/features/2026-07-19-recipe-switch-performance.md` Phase C).
  //
  // Every `/app*` destination — the dashboard, the library grid, the recipe
  // workspace and its sub-mode segments, the tray destinations (Feed, Discover,
  // Cooks, Groups, Custom logic, Syntax help) and the unknown-path fallback — is
  // a CHILD of this object. The table and the full rationale live in
  // `routes/app-routes.ts`; the short version:
  //
  //   • One route object ⇒ one <AppRoute /> instance by construction. The open
  //     recipe, the warm sync connection, the editor and the evaluator worker
  //     survive every library↔recipe crossing.
  //   • Before this the property held only by ACCIDENT of React reusing two
  //     same-type elements rendered at the same depth (measured: ONE AppRoute
  //     mount across library → A → library → B on the old table too). Wrapping
  //     any one of the old sibling objects in a <Suspense> / provider / layout
  //     would have silently reinstated a full remount per crossing — the same
  //     probe recorded 4 mounts / 3 unmounts with one wrapper added.
  //   • The children carry no element: AppRoute picks the surface from
  //     deriveAppView(pathname) and renders no <Outlet/>. Child params still
  //     reach it — React Router shares ONE accumulated params object across a
  //     matched branch, so useParams() here reads :slug and :groupUuid.
  //
  // Shell-persistence fix (2026-07-16) folded Feed / Cooks / Groups / Custom
  // logic / Syntax help in here first; those genuinely DID unmount the shell,
  // because their route elements were different components (design.md §6.0a;
  // the operator "two moves" bug).
  //
  // The ?sub= query param is DROPPED — no redirect shim needed. Gap doc A.3.3 /
  // A.11.1. Redesign plan §13.4 P3.NAV. AppRoute handles auth-gating and
  // redirects unauthenticated users to SignInGate, preserving the deep-link
  // target via returnTo.
  {
    path: "/app",
    element: <AppRoute />,
    children: APP_ROUTE_CHILDREN,
  },
  // /app/r/:slug, its sub-mode segments, and the /app/* fallback are no longer
  // top-level route objects — they are CHILDREN of the `/app` object above
  // (route unification, 2026-07-24). The P0 remount fix of 2026-06-15 (ONE
  // parent owning the single <AppRoute /> element across Cook↔Edit) is
  // preserved verbatim, one level deeper: see `routes/app-routes.ts`. Custom
  // logic, Syntax help, the Cooks ledger and Family groups moved there in the
  // 2026-07-16 shell-persistence fix; WorkspaceContainer renders each in-shell
  // via deriveAppView(pathname).
  //
  // `/app/join/:token` and `/app/friend-invite/:token` stay STANDALONE: they are
  // pre-auth landings that resolve a token without the app shell.
  {
    path: "/app/join/:token",
    element: (
      <Suspense fallback={null}>
        <JoinRoute />
      </Suspense>
    ),
  },
  {
    // Friend-invite landing (B-WP11c). Own route element (like /app/join) so it
    // resolves session independently of the heavyweight /app shell. Its static
    // segments outrank the `*` child of /app, so it wins the match regardless of
    // declaration order (pinned by app.route-unification.test.tsx).
    path: "/app/friend-invite/:token",
    element: (
      <Suspense fallback={null}>
        <FriendInviteRoute />
      </Suspense>
    ),
  },
  // The former top-level `/app/*` catch-all is now the `{ path: "*" }` child of
  // the `/app` object above (routes/app-routes.ts), so an unknown `/app` path
  // resolves through the SAME AppRoute instance instead of a second element.
  // Public routes — same SPA bundle, no auth required.
  // Wave PERF-2.A — lazy-loaded behind <Suspense fallback={null}>. Their
  // bundles are never needed on the authenticated `/app` critical path.
  // Empty-fallback is acceptable: these routes are first-paint surfaces with no
  // perceived "switch" — the brief blank between SW resolution and chunk-arrival
  // is bounded by the network roundtrip.
  // /r/:publicUuid — fully-fledged Share view (Track 3.G).
  {
    path: "/r/:publicUuid",
    element: (
      <Suspense fallback={null}>
        <RecipeShareView />
      </Suspense>
    ),
  },
  // /u/:handle — public user profile page (PR9).
  {
    path: "/u/:handle",
    element: (
      <Suspense fallback={null}>
        <UserProfileView />
      </Suspense>
    ),
  },
  // /u/:handle/:slug — the per-recipe public URL (R4 WP-1). SAME component as
  // /r/:publicUuid: RecipeShareView fetches by-slug when the route gives it a
  // handle + slug, by uuid otherwise. /r/:publicUuid stays live as the permanent
  // link form; this is the readable, canonical one (see share/recipe.tsx for the
  // recorded functionality.md §3.1 deviation and its canonical-tag mitigation).
  {
    path: "/u/:handle/:slug",
    element: (
      <Suspense fallback={null}>
        <RecipeShareView />
      </Suspense>
    ),
  },
  // /discover — public Discover-lite boards (B-WP7). Signed-out-readable public
  // acquisition surface; reached from the Share-page footer link. Deliberately
  // NO app primary-nav entry this WP (feature plan B-WP7 §d) — the signed-in nav
  // entry lands with B-WP9.
  {
    path: "/discover",
    element: (
      <Suspense fallback={null}>
        <DiscoverView />
      </Suspense>
    ),
  },
  // /groups — PUBLIC groups directory (B-WP12). Reachable signed-out.
  {
    path: "/groups",
    element: (
      <Suspense fallback={null}>
        <GroupsDirectoryView />
      </Suspense>
    ),
  },
  // /g/:groupUuid — PUBLIC group page (B-WP12). A private group 404s here.
  {
    path: "/g/:groupUuid",
    element: (
      <Suspense fallback={null}>
        <PublicGroupView />
      </Suspense>
    ),
  },
  // /docs/syntax — PUBLIC grammar reference (R4 WP-2). Outside /app/*, so no
  // session is resolved. Same component as /app/help; the marketing footer
  // links here with a full cross-app navigation.
  {
    path: "/docs/syntax",
    element: (
      <Suspense fallback={null}>
        <DocsSyntaxView />
      </Suspense>
    ),
  },
  // /admin/* — admin panel (Track 4.P).
  // AdminGuard calls admin.ping on mount; non-admin redirects to /.
  // /admin/* routes are excluded from the Workbox precache manifest (sw.ts).
  // §4.9.2: Wrapped in Suspense so the lazy chunks load on demand without
  // blocking the main bundle. fallback={null} is intentional — the admin
  // surface is invite-only and a brief blank is acceptable.
  {
    path: "/admin",
    element: (
      <Suspense fallback={null}>
        <AdminGuard>
          <AdminShell />
        </AdminGuard>
      </Suspense>
    ),
    children: [
      {
        index: true,
        element: (
          <Suspense fallback={null}>
            <AdminOverview />
          </Suspense>
        ),
      },
      {
        path: "people",
        element: (
          <Suspense fallback={null}>
            <AdminPeople />
          </Suspense>
        ),
      },
      {
        path: "queue",
        element: (
          <Suspense fallback={null}>
            <AdminQueue />
          </Suspense>
        ),
      },
      {
        path: "system",
        element: (
          <Suspense fallback={null}>
            <AdminSystem />
          </Suspense>
        ),
      },
      {
        path: "analytics",
        element: (
          <Suspense fallback={null}>
            <AdminAnalytics />
          </Suspense>
        ),
      },
      // Legacy deep-links from before the five-section consolidation keep working:
      // Waitlist / Invites / Users → People; Feedback / Moderation → Queue;
      // Crashes / Audit / Maintenance → System. A per-user detail link resolves
      // into the People search that surfaces that account.
      { path: "waitlist", element: <Navigate to="/admin/people?status=waiting" replace /> },
      { path: "invites", element: <Navigate to="/admin/people?status=invited" replace /> },
      { path: "users", element: <Navigate to="/admin/people" replace /> },
      { path: "users/:userUuid", element: <LegacyUserRedirect /> },
      { path: "feedback", element: <Navigate to="/admin/queue?type=feedback" replace /> },
      { path: "moderation", element: <Navigate to="/admin/queue?type=reports" replace /> },
      { path: "crashes", element: <Navigate to="/admin/system" replace /> },
      { path: "audit", element: <Navigate to="/admin/system" replace /> },
      { path: "maintenance", element: <Navigate to="/admin/system" replace /> },
    ],
  },
  // Wave 2 (onboarding-empty-r1): /onboarding-demo route removed — wizard
  // wishlisted per `.plan/wishlist/onboarding-wizard.md`.
  // Wave 7 (2026-05-12): all dev demo routes removed. Workspace is the
  // production recipe surface; other demos wishlisted at
  // .plan/wishlist/2026-05-12-dev-demo-routes.md for revival under /_dev/*.
  // Phase 2b capsule prototype — dev preview only.
  // Validates safe-area, auto-minimise, keyboard-hide, and
  // prefers-reduced-transparency fallback on real iOS Safari PWA.
  // The route is registered only when import.meta.env.DEV is true so Vite
  // removes it (and the lazy import above) from the production bundle.
  ...(import.meta.env.DEV && ShellPrototypePage
    ? [
        {
          path: "/shell-prototype",
          element: (
            <Suspense fallback={null}>
              <ShellPrototypePage />
            </Suspense>
          ),
        },
      ]
    : []),
  // /pip-demo — Pip mascot QA surface (dev only, plan Wave 1).
  // Registered only in dev builds; Vite removes this route and the lazy import
  // from the production bundle.
  ...(import.meta.env.DEV && PipDemoRoute
    ? [
        {
          path: "/pip-demo",
          element: (
            <Suspense fallback={null}>
              <PipDemoRoute />
            </Suspense>
          ),
        },
      ]
    : []),
  {
    path: "*",
    element: <NotFound />,
  },
]);

// ─── Mount ────────────────────────────────────────────────────────────────────

const rootEl = document.getElementById("root");
if (!rootEl) throw new Error("Root element #root not found in document.");

// Track 4.R: ErrorBoundary wraps the entire React tree (outside the router)
// so a route-level crash does not unmount everything. The boundary catches
// any React render/lifecycle error and shows the "Something broke." fallback.
//
// AutoUpdater mounts at the app root (outside the router) so update handling
// is active on every route — including /sign-in and Share-view surfaces. It
// is headless (renders nothing): it registers the Workbox service worker via
// useRegisterSW() (architecture.md §4.6), detects updates via the waiting-SW
// needRefresh flag OR the version probe's external flag, and applies them
// silently — immediately when the update is detected on page load or right
// after the user returns to the tab (the arrival window, where a reload is
// invisible), otherwise at the first safe moment: document hidden or 30 s
// without interaction. See features/pwa/AutoUpdater.tsx.
createRoot(rootEl).render(
  <StrictMode>
    <ErrorBoundary>
      <RouterProvider router={router} />
      <AutoUpdater />
      {/* Wave PERF-1 — dev-only timeline panel; renders only when ?perf=1 is
          in the URL AND PERF_ENABLED is true. No-op + tree-shaken in prod. */}
      <PerfOverlay />
    </ErrorBoundary>
  </StrictMode>,
);

// Re-export __version so index.ts stub is superseded cleanly.
export const __version = "0.0.0";
